import { Allocation, Split } from '@eppo/js-client-sdk-common/dist/interfaces'
import { EppoConfiguration } from '../../types'
import { OperatorType, Rule } from './eppoRuleType'

// Constants for shard range validation
const FULL_RANGE_START = 0
const FULL_RANGE_END = 10000

/**
 * Checks if an allocation is bot-friendly by validating it has no custom rules
 * and uses either default rules or covers the full traffic range (0-10000)
 */
const isAllocationBotFriendly = (allocation: Allocation): boolean => {
  // Allocations with custom rules are not bot-friendly
  if (allocation.rules && !rulesTargetBots(allocation.rules)) {
    return false
  }

  const firstSplit = allocation.splits[0]

  // Default rules have no shards - these are bot-friendly
  if (firstSplit.shards.length === 0) {
    return true
  }

  // Check if the allocation covers the full traffic range
  return coversFullTrafficRange(firstSplit)
}

const rulesTargetBots = (rules: Rule[]): boolean => {
  return rules.every(rule => {
    return rule.conditions.every(condition => {
      return (
        condition.attribute === 'isBot' &&
        condition.operator === OperatorType.ONE_OF &&
        condition.value.every(v => v === 'true')
      )
    })
  })
}

/**
 * Determines if a split covers the full traffic range (0-10000)
 */
const coversFullTrafficRange = (split: Split): boolean => {
  const firstRange = split.shards[0].ranges[0]
  const { start, end } = firstRange

  return start === FULL_RANGE_START && end === FULL_RANGE_END
}

/**
 * Filters an Eppo configuration to keep only bot-friendly allocations.
 * Bot-friendly allocations are those without custom rules that either:
 * - Use default rules (no shards)
 * - Cover the full traffic range (0-10000)
 */
export const filterBotFriendlyAllocations = (
  configuration: EppoConfiguration,
): EppoConfiguration => {
  return Object.entries(configuration).reduce<EppoConfiguration>(
    (config, [key, value]) => {
      const allocations = value.allocations.filter(isAllocationBotFriendly)

      return { ...config, [key]: { ...value, allocations } }
    },
    {},
  )
}
